import React from 'react';

/**
 * A full-window page that can actually scroll.
 *
 * WHY THIS EXISTS, AND WHY EVERY FULL-SCREEN PAGE MUST USE IT
 *
 * `carstyles.css` sets `body, .page { overflow: hidden }` GLOBALLY. There is no
 * document scroll anywhere in reactor to fall back on. So a box that fills the
 * window either owns an `overflow-y-auto` itself or its overflow is not merely
 * below the fold — it is UNREACHABLE. A wheel moves nothing, and there is no
 * scrollbar to tell anyone something is missing.
 *
 * Combined with `justify-center` that produces the worst version of the bug:
 * the excess is split across BOTH edges, so a tall card loses its header and
 * its footer at the same moment. The sign-in screen was measured at card
 * top = -19px, with the logo clipped off the top while "Reset password" sat off
 * the bottom, on an ordinary 1366x660 laptop panel. It was fine at 1440x900,
 * which is exactly why it survived so long.
 *
 * TWO RULES, both of which are load-bearing:
 *
 *   1. The OUTER box is `fixed inset-0 overflow-y-auto` — it is the scroll
 *      container, and nothing else will be.
 *   2. The INNER box is `min-h-full`, NEVER `min-h-screen`. `min-h-screen`
 *      forces at least 100vh regardless of the content's real height, which
 *      leaves the scroll container with nothing to scroll — and on a phone,
 *      100vh counts a URL bar that is currently on screen.
 *
 * The `short:` breakpoint (`max-height: 720px`, the only height breakpoint in
 * tailwind.config.js) trims the vertical padding so 768/720 laptop panels fit
 * outright rather than merely scrolling. Those panels are `lg` by WIDTH, which
 * is why a width breakpoint could never have caught them.
 *
 * `scripts/shot-login.mjs` is the check, across six real viewports.
 */
export default function FullScreenScroll({ children, className = '', innerClassName = '' }) {
  return (
    <div className={`fixed inset-0 overflow-y-auto bg-gray-100 ${className}`}>
      <div className={`flex min-h-full flex-col justify-center px-4 py-10 short:py-6 lg:px-6 ${innerClassName}`}>
        {children}
      </div>
    </div>
  );
}
